import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api, fmtDate, formatApiError, exportToExcel } from "@/lib/api";
import { PageHeader } from "@/components/Layout";
import { Modal, Btn, Select, Input, EmptyState, ConfirmDialog, CreatableSelect, ListSkeleton } from "@/components/Kit";
import { IconBtn } from "@/pages/Cotizaciones";
import { OrderItemsEditor } from "@/components/OrderItemsEditor";
import { TagsField, TagsCell } from "@/components/TagsInput";
import { FilterBar, matchFilters, sortByFolio, FolioSortHeader } from "@/components/FilterBar";
import { ImportDialog } from "@/components/ImportDialog";
import { FolioSelector } from "@/components/FolioSelector";
import { useRowSelection, SelectCheckbox, BulkDeleteBar } from "@/components/BulkSelect";
import { usePagination, Pagination } from "@/components/Pagination";
import { toast } from "sonner";
import { Plus, Upload, Eye, Download, Link2, Pencil, Archive, ArchiveRestore, Copy, CircleSlash } from "lucide-react";
import { StatusBadge, FolioTag } from "@/components/StatusBadge";
import { useAuth } from "@/context/AuthContext";

const STATUSES = ["Impresión Interna", "Impresión Externa", "Pendiente", "Acabado Interno", "Acabado Externo", "Terminada", "Entregada"];

const STATUS_STYLES = {
  "Impresión Interna": "bg-[#FACC15] text-[#713F12] border-[#EAB308]",
  "Impresión Externa": "bg-[#FACC15] text-[#713F12] border-[#EAB308]",
  "Pendiente": "bg-[#EF4444] text-white border-[#DC2626]",
  "Acabado Interno": "bg-[#FDBA74] text-[#7C2D12] border-[#FB923C]",
  "Acabado Externo": "bg-[#EA580C] text-white border-[#C2410C]",
  "Terminada": "bg-[#22C55E] text-white border-[#16A34A]",
  "Entregada": "bg-[#15803D] text-white border-[#166534]",
};
const normalizeStatus = (s) => (STATUSES.includes(s) ? s : "Pendiente");
const todayIso = () => new Date().toISOString().slice(0, 10);

function OrderStatusSelect({ value, onChange, testid }) {
  const v = normalizeStatus(value);
  return (
    <select
      value={v}
      onChange={(e) => onChange(e.target.value)}
      data-testid={testid}
      className={`border px-2 py-1 font-mono-data text-xs font-bold cursor-pointer focus:outline-none focus:ring-1 focus:ring-[#465B7F] ${STATUS_STYLES[v] || "bg-white text-[#16202B]"}`}
    >
      {STATUSES.map((s) => (
        <option key={s} value={s} className="bg-white text-[#16202B] font-normal">{s}</option>
      ))}
    </select>
  );
}

// Deriva un título para la OT desde la descripción del primer artículo de la remisión:
// toma la corrida inicial de palabras en MAYÚSCULAS; si no hay, usa la primera oración.
function tituloDesdeDescripcion(desc) {
  if (!desc) return "";
  const texto = String(desc).trim();
  const tokens = texto.split(/\s+/);
  const mayus = [];
  for (const t of tokens) {
    const limpio = t.replace(/[.,;:¡!¿?()"'*]/g, "");
    const letras = limpio.replace(/[^A-Za-zÁÉÍÓÚÜÑáéíóúüñ]/g, "");
    const esMayus = letras.length > 0 && letras === letras.toUpperCase() && letras !== letras.toLowerCase();
    if (esMayus || (mayus.length && limpio && !letras.length)) {
      if (limpio) mayus.push(limpio);
      if (/[.!?]/.test(t) || mayus.length >= 8) break; // corta en fin de oración o límite
    } else {
      break;
    }
  }
  if (mayus.length) return mayus.join(" ");
  const primera = texto.split(/(?<=[.!?])\s/)[0] || texto;
  return primera.replace(/[.!?]+$/, "").trim();
}


export default function Ordenes() {
  const navigate = useNavigate();
  const { canDo } = useAuth();
  const oCreate = canDo("ordenes", "create");
  const oEdit = canDo("ordenes", "edit");
  const oCancel = canDo("ordenes", "cancel");
  const [searchParams, setSearchParams] = useSearchParams();
  const [orders, setOrders] = useState([]);
  const [clients, setClients] = useState([]);
  const [remisiones, setRemisiones] = useState([]);
  const [products, setProducts] = useState([]);
  const [jobTypes, setJobTypes] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ client_id: "", title: "", description: "", assigned_to: "", due_date: "", tipo_trabajo: "", maquina: "", acabados: "", material: "", status: "Pendiente", remision_id: "" });
  const [items, setItems] = useState([{ description: "", quantity: 1, product_id: null, maquina: "", acabados: "", material: "" }]);
  const [createdDate, setCreatedDate] = useState(todayIso());
  const [tags, setTags] = useState([]);
  const [deleteOpen, setDeleteOpen] = useState(null);
  const [filters, setFilters] = useState({});
  const [folioDir, setFolioDir] = useState("desc");
  const [importOpen, setImportOpen] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [assignOpen, setAssignOpen] = useState(null);
  const [assignRid, setAssignRid] = useState("");
  const [view, setView] = useState("general");
  const [notifyUsers, setNotifyUsers] = useState([]);
  const [notifyEmail, setNotifyEmail] = useState("");
  const [loading, setLoading] = useState(true);

  const loadOrders = () => api.get(`/work-orders?archived=${view === "archived"}`).then((r) => setOrders(r.data));
  useEffect(() => {
    api.get("/clients").then((r) => setClients([...r.data].sort((a, b) => (a.name || "").localeCompare(b.name || "", "es", { sensitivity: "base" }))));
    api.get("/products?active=true").then((r) => setProducts(r.data));
    api.get("/job-types").then((r) => setJobTypes(r.data));
    api.get("/remisiones").then((r) => setRemisiones(r.data));
    api.get("/notices/users").then((r) => setNotifyUsers(r.data)).catch(() => setNotifyUsers([]));
  }, []);
  useEffect(() => {
    setLoading(true);
    api.get(`/work-orders?archived=${view === "archived"}`).then((r) => setOrders(r.data)).finally(() => setLoading(false));
  }, [view]);

  useEffect(() => {
    const rid = searchParams.get("fromRemision");
    if (rid && remisiones.length) {
      const r = remisiones.find((x) => x.id === rid);
      if (r) {
        openNewFromRemision(r);
        setSearchParams({}, { replace: true });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, remisiones]);

  useEffect(() => {
    const eid = searchParams.get("edit");
    const did = searchParams.get("duplicate");
    const target = eid || did;
    if (target && orders.length) {
      const o = orders.find((x) => x.id === target);
      if (o) {
        eid ? openEdit(o) : openDuplicate(o);
        setSearchParams({}, { replace: true });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, orders]);

  const canArchive = (o) => !!o.remision_id && o.status === "Entregada";
  const archive = async (o) => {
    if (!canArchive(o)) {
      return toast.error("Solo puedes archivar una OT que tenga remisión asignada y estatus 'Entregada'.");
    }
    try {
      await api.post(`/work-orders/${o.id}/archive`);
      toast.success(`Orden ${o.number} archivada`);
      loadOrders();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };
  const unarchive = async (o) => {
    try {
      await api.post(`/work-orders/${o.id}/unarchive`);
      toast.success(`Orden ${o.number} restaurada al listado general`);
      loadOrders();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const filteredOrders = useMemo(
    () => sortByFolio(orders.filter((o) => matchFilters(o, filters)), folioDir),
    [orders, filters, folioDir]
  );

  const sel = useRowSelection(filteredOrders);
  const pageOrders = usePagination(filteredOrders, 50, { persist: true });

  const exportExcel = async () => {
    try {
      const headers = ["Folio", "Título", "Cliente", "Remisión", "Tipo de trabajo", "Tags", "Responsable", "Entrega", "Estatus"];
      const rows = filteredOrders.map((o) => [
        o.number, o.title || "", o.client?.name || "", o.remision_number || "", o.tipo_trabajo || "",
        (o.tags || []).join(", "), o.assigned_to || "", o.due_date ? fmtDate(o.due_date) : "", o.status || "",
      ]);
      const by = {};
      let tc = 0;
      filteredOrders.forEach((o) => {
        const s = o.status || "Sin estatus";
        by[s] = (by[s] || 0) + 1;
        tc++;
      });
      const summaryRows = Object.entries(by).map(([s, c]) => [s, c]);
      summaryRows.push(["TOTAL GENERAL", tc]);
      const summary = { title: "Resumen", headers: ["Estatus", "Cantidad"], rows: summaryRows };
      await exportToExcel("ordenes_de_trabajo", "Órdenes", headers, rows, summary);
    } catch {
      toast.error("No se pudo exportar a Excel");
    }
  };

  const openAssign = (o) => {
    setAssignRid(o.remision_id || "");
    setAssignOpen(o);
  };

  const saveAssign = async () => {
    try {
      await api.put(`/work-orders/${assignOpen.id}/remision`, { remision_id: assignRid || null });
      toast.success(assignRid ? "Remisión asignada a la orden" : "Remisión quitada de la orden");
      setAssignOpen(null);
      loadOrders();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const clientOptions = useMemo(
    () => [...clients].sort((a, b) => (a.name || "").localeCompare(b.name || "", "es", { sensitivity: "base" })),
    [clients]
  );

  const createProduct = async (payload) => {
    try {
      const { data } = await api.post("/products", payload);
      setProducts((prev) => [data, ...prev]);
      toast.success("Producto creado");
      return data;
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
      return null;
    }
  };

  const [folioOpts, setFolioOpts] = useState(null);
  const [folioSel, setFolioSel] = useState("");
  const loadFolioOptions = () => {
    setFolioSel("");
    setFolioOpts(null);
    api.get("/folios/options?prefix=OT").then((r) => setFolioOpts(r.data)).catch(() => setFolioOpts(null));
  };

  const createJobType = async (name) => {
    try {
      await api.post("/job-types", { name });
      setJobTypes((prev) => [...prev, name].sort((a, b) => a.localeCompare(b, "es", { sensitivity: "base" })));
      toast.success("Tipo de trabajo agregado");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const openNew = () => {
    setEditing(null);
    setForm({ client_id: "", title: "", description: "", assigned_to: "", due_date: "", tipo_trabajo: "", maquina: "", acabados: "", material: "", status: "Pendiente", remision_id: "" });
    setItems([{ description: "", quantity: 1, product_id: null, maquina: "", acabados: "", material: "" }]);
    setCreatedDate(todayIso());
    setTags([]);
    setNotifyEmail("");
    loadFolioOptions();
    setOpen(true);
  };

  const openNewFromRemision = (r) => {
    setEditing(null);
    const primeraDesc = (r.items && r.items[0] && r.items[0].description) || "";
    setForm({
      client_id: r.client_id || "",
      title: tituloDesdeDescripcion(primeraDesc),
      description: "",
      assigned_to: "",
      due_date: "",
      tipo_trabajo: "",
      maquina: "",
      acabados: "",
      material: "",
      status: "Pendiente",
      remision_id: r.id,
    });
    setItems(r.items && r.items.length
      ? r.items.map((it) => ({ description: it.description, quantity: it.quantity, product_id: it.product_id || null, maquina: it.maquina || "", acabados: it.acabados || "", material: it.material || "" }))
      : [{ description: "", quantity: 1, product_id: null, maquina: "", acabados: "", material: "" }]);
    setCreatedDate(todayIso());
    setTags([]);
    setNotifyEmail("");
    loadFolioOptions();
    setOpen(true);
  };

  const openEdit = (o) => {
    setEditing(o.id);
    setForm({
      client_id: o.client_id || "",
      title: o.title || "",
      description: o.description || "",
      assigned_to: o.assigned_to || "",
      due_date: o.due_date || "",
      tipo_trabajo: o.tipo_trabajo || "",
      status: normalizeStatus(o.status),
      remision_id: o.remision_id || "",
    });
    setItems(o.items && o.items.length ? o.items.map((it) => ({ description: it.description, quantity: it.quantity, product_id: it.product_id || null, maquina: it.maquina || "", acabados: it.acabados || "", material: it.material || "" })) : [{ description: "", quantity: 1, product_id: null, maquina: "", acabados: "", material: "" }]);
    setCreatedDate((o.created_at || todayIso()).slice(0, 10));
    setTags(o.tags || []);
    setNotifyEmail(o.notify_email || "");
    setOpen(true);
  };

  const openDuplicate = (o) => {
    setEditing(null);
    setForm({
      client_id: o.client_id || "",
      title: o.title || "",
      description: o.description || "",
      assigned_to: o.assigned_to || "",
      due_date: o.due_date || "",
      tipo_trabajo: o.tipo_trabajo || "",
      status: normalizeStatus(o.status),
      remision_id: o.remision_id || "",
    });
    setItems(o.items && o.items.length ? o.items.map((it) => ({ description: it.description, quantity: it.quantity, product_id: it.product_id || null, maquina: it.maquina || "", acabados: it.acabados || "", material: it.material || "" })) : [{ description: "", quantity: 1, product_id: null, maquina: "", acabados: "", material: "" }]);
    setCreatedDate(todayIso());
    setTags([...(o.tags || [])]);
    setNotifyEmail("");
    loadFolioOptions();
    setOpen(true);
  };

  const selectRemision = (rid) => {
    if (!rid) {
      setForm((f) => ({ ...f, remision_id: "" }));
      return;
    }
    const r = remisiones.find((x) => x.id === rid);
    if (!r) {
      setForm((f) => ({ ...f, remision_id: rid }));
      return;
    }
    // Al asignar una remisión: cliente y conceptos se toman de la remisión.
    setForm((f) => ({ ...f, remision_id: rid, client_id: r.client_id || f.client_id }));
    setItems(
      r.items && r.items.length
        ? r.items.map((it) => ({ description: it.description, quantity: it.quantity, product_id: it.product_id || null, maquina: it.maquina || "", acabados: it.acabados || "", material: it.material || "" }))
        : [{ description: "", quantity: 1, product_id: null, maquina: "", acabados: "", material: "" }]
    );
  };

  const save = async (e) => {
    e.preventDefault();
    if (!form.client_id) return toast.error("Selecciona un cliente");
    if (!form.title) return toast.error("El título es obligatorio");
    if (!tags || tags.length === 0) return toast.error("Agrega al menos un tag (etiqueta) para guardar el documento");
    if (!notifyEmail) return toast.error("Selecciona a quién notificar (aviso crítico)");
    const payload = {
      client_id: form.client_id,
      remision_id: form.remision_id || null,
      title: form.title,
      description: form.description,
      assigned_to: form.assigned_to,
      due_date: form.due_date,
      tipo_trabajo: form.tipo_trabajo,
      status: form.status,
      items,
      tags,
      notify_email: notifyEmail,
      ...(editing ? {} : { folio: folioSel || null }),
    };
    try {
      if (editing) {
        await api.put(`/work-orders/${editing}`, payload);
        toast.success("Orden actualizada");
      } else {
        const { data } = await api.post("/work-orders", payload);
        if (folioSel && data?.number && data.number !== folioSel) {
          toast.info(`El folio ${folioSel} ya se ocupó; se asignó ${data.number}`);
        } else {
          toast.success("Orden creada");
        }
        setOpen(false);
        if (data?.id) return navigate(`/documento/orden/${data.id}`);
      }
      setOpen(false);
      loadOrders();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const changeStatus = async (id, status) => {
    await api.put(`/work-orders/${id}`, { status });
    loadOrders();
  };

  const remove = async () => {
    if (!deleteOpen) return;
    try {
      await api.delete(`/work-orders/${deleteOpen.id}`);
      toast.success(`Orden ${deleteOpen.number} eliminada`);
      setDeleteOpen(null);
      loadOrders();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const bulkRemove = async () => {
    try {
      await api.post(`/work-orders/bulk-delete`, { ids: sel.selectedIds });
      toast.success(`${sel.selectedIds.length} órdenes eliminadas`);
      sel.clear();
      setBulkOpen(false);
      loadOrders();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <>
      <PageHeader
        overline="Ciclo de venta · Paso 2"
        title="Órdenes de Trabajo"
        action={
          <div className="flex gap-3">
            <Btn variant="outline" onClick={exportExcel} data-testid="export-ordenes-button" disabled={orders.length === 0}>
              <Download size={16} /> Exportar a Excel
            </Btn>
            {oCreate && (
              <Btn variant="outline" onClick={() => setImportOpen(true)} data-testid="import-ordenes-button">
                <Upload size={16} /> Importar
              </Btn>
            )}
            {oCreate && (
              <Btn onClick={openNew} data-testid="new-order-button" disabled={clients.length === 0}>
                <Plus size={16} /> Nueva orden
              </Btn>
            )}
          </div>
        }
      />
      <div className="p-10">
        <div className="flex gap-1 mb-6 border-b border-[#E3E8EE]" data-testid="order-view-tabs">
          {[
            { k: "general", label: "Listado general" },
            { k: "archived", label: "Órdenes archivadas" },
          ].map((t) => (
            <button
              key={t.k}
              onClick={() => setView(t.k)}
              data-testid={`order-tab-${t.k}`}
              className={`px-4 py-2 text-sm font-mono-data uppercase tracking-wider border-b-2 -mb-px transition-colors ${view === t.k ? "border-[#465B7F] text-[#465B7F] font-bold" : "border-transparent text-[#5B6672] hover:text-[#16202B]"}`}
            >
              {t.label}
            </button>
          ))}
        </div>
        {loading ? (
          <ListSkeleton testid="ordenes-loading" />
        ) : orders.length === 0 ? (
          <EmptyState
            title={view === "archived" ? "Sin órdenes archivadas" : "Sin órdenes de trabajo"}
            subtitle={view === "archived" ? "Las órdenes que archives aparecerán aquí." : "Genera órdenes desde una remisión o créalas manualmente."}
            action={view === "general" && oCreate && clients.length > 0 && <Btn onClick={openNew}><Plus size={16} /> Nueva orden</Btn>}
          />
        ) : (
          <>
          <FilterBar clients={clients} reportType="ordenes" onApply={setFilters} jobTypes={jobTypes} statuses={[...new Set(orders.map((o) => o.status).filter(Boolean))].sort((a, b) => a.localeCompare(b, "es"))} showTags />
          {oCancel && <BulkDeleteBar count={sel.selectedIds.length} onDelete={() => setBulkOpen(true)} testid="bulk-delete-ordenes" />}
          <div className="border border-[#E3E8EE] bg-[#FFFFFF] data-scroll">
            <table className="w-full">
              <thead>
                <tr className="border-b border-[#E3E8EE] text-left">
                  <Th className="w-10">{oCancel && <SelectCheckbox checked={sel.allChecked} onChange={sel.toggleAll} testid="select-all-ordenes" />}</Th>
                  <Th><FolioSortHeader dir={folioDir} onToggle={() => setFolioDir((d) => d === "desc" ? "asc" : "desc")} /></Th>
                  <Th>Título</Th>
                  <Th>Cliente</Th>
                  <Th>Remisión</Th>
                  <Th>Tipo de trabajo</Th>
                  <Th>Tags</Th>
                  <Th>Responsable</Th>
                  <Th>Entrega</Th>
                  <Th>Estatus</Th>
                  <Th className="text-right">Acciones</Th>
                </tr>
              </thead>
              <tbody>
                {pageOrders.pageItems.map((o) => (
                  <tr key={o.id} data-testid={`order-row-${o.number}`} onClick={() => openEdit(o)} className="border-b border-[#E3E8EE] last:border-0 hover:bg-[#E9EEF3] transition-colors cursor-pointer">
                    <Td onClick={(e) => e.stopPropagation()}>{oCancel && <SelectCheckbox checked={sel.selected.has(o.id)} onChange={() => sel.toggle(o.id)} testid={`select-order-${o.number}`} />}</Td>
                    <Td className="font-medium"><FolioTag number={o.number} kind="ot" testid={`order-folio-${o.number}`} /></Td>
                    <Td className="text-[#1C1A17]">{o.title}</Td>
                    <Td>{o.client?.name || "—"}</Td>
                    <Td onClick={(e) => e.stopPropagation()}>
                      {o.remision_number ? (
                        <span className="inline-flex items-center gap-1.5" data-testid={`order-remision-${o.number}`}>
                          <FolioTag number={o.remision_number} kind="remision" />
                          <IconBtn title="Cambiar remisión" onClick={() => openAssign(o)} testid={`edit-remision-${o.number}`}><Pencil size={13} /></IconBtn>
                        </span>
                      ) : (
                        <button
                          onClick={() => openAssign(o)}
                          data-testid={`assign-remision-${o.number}`}
                          className="inline-flex items-center gap-1 text-xs font-mono-data text-[#465B7F] border border-[#465B7F]/40 px-2 py-1 hover:bg-[#465B7F]/10 transition-colors"
                        >
                          <Link2 size={12} /> Asignar
                        </button>
                      )}
                    </Td>
                    <Td>{o.tipo_trabajo ? <span className="inline-block px-2 py-0.5 bg-[#465B7F]/10 text-[#374A66] text-xs" data-testid={`order-tipo-${o.number}`}>{o.tipo_trabajo}</span> : "—"}</Td>
                    <Td><TagsCell tags={o.tags} testid={`order-tags-${o.number}`} /></Td>
                    <Td>{o.assigned_to || "—"}</Td>
                    <Td>{fmtDate(o.due_date)}</Td>
                    <Td>
                      <div onClick={(e) => e.stopPropagation()}>
                      {!oEdit || o.status === "Anulado" ? <StatusBadge status={o.status} /> : <OrderStatusSelect value={o.status} onChange={(s) => changeStatus(o.id, s)} testid={`order-status-${o.number}`} />}
                      </div>
                    </Td>
                    <Td className="text-right whitespace-nowrap">
                      <div onClick={(e) => e.stopPropagation()}>
                      {view === "archived" ? (
                        <>
                        <IconBtn title="Ver documento" onClick={() => navigate(`/documento/orden/${o.id}`)} testid={`view-order-${o.number}`}><Eye size={15} /></IconBtn>
                        {oEdit && <IconBtn title="Desarchivar" onClick={() => unarchive(o)} testid={`unarchive-order-${o.number}`}><ArchiveRestore size={15} /></IconBtn>}
                        {oCancel && <IconBtn title="Anular Documento" tone="anular" onClick={() => setDeleteOpen(o)} testid={`delete-order-${o.number}`}><CircleSlash size={15} /></IconBtn>}
                        </>
                      ) : (
                        <>
                        {oEdit && <IconBtn title="Editar" onClick={() => openEdit(o)} testid={`edit-order-${o.number}`}><Pencil size={15} /></IconBtn>}
                        {oCreate && <IconBtn title="Duplicar" onClick={() => openDuplicate(o)} testid={`duplicate-order-${o.number}`}><Copy size={15} /></IconBtn>}
                        <IconBtn title="Ver documento" onClick={() => navigate(`/documento/orden/${o.id}`)} testid={`view-order-${o.number}`}><Eye size={15} /></IconBtn>
                        {oEdit && <IconBtn
                          title={canArchive(o) ? "Archivar" : "Para archivar, la OT debe tener remisión asignada y estatus 'Entregada'"}
                          onClick={() => archive(o)}
                          testid={`archive-order-${o.number}`}
                        >
                          <Archive size={15} className={canArchive(o) ? "" : "opacity-40"} />
                        </IconBtn>}
                        {oCancel && <IconBtn title="Anular Documento" tone="anular" onClick={() => setDeleteOpen(o)} testid={`delete-order-${o.number}`}><CircleSlash size={15} /></IconBtn>}
                        </>
                      )}
                      </div>
                    </Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Pagination {...pageOrders} testid="pagination-ordenes" />
          </>
        )}
      </div>

      <Modal open={open} onClose={() => setOpen(false)} title={editing ? "Editar orden de trabajo" : "Nueva orden de trabajo"} xl logo>
        <form onSubmit={save} className="space-y-5">
          {!editing && <FolioSelector options={folioOpts} value={folioSel} onChange={setFolioSel} />}
          <div className="grid grid-cols-2 gap-4">
            <Select label="Cliente *" value={form.client_id} onChange={set("client_id")} testid="order-client-select">
              <option value="">Selecciona…</option>
              {clientOptions.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </Select>
            <CreatableSelect
              label="Tipo de trabajo"
              value={form.tipo_trabajo}
              onChange={(v) => setForm((f) => ({ ...f, tipo_trabajo: v }))}
              options={jobTypes}
              onCreate={createJobType}
              placeholder="Selecciona o agrega…"
              testid="order-tipo-trabajo"
            />
          </div>
          <Input label="Título del trabajo *" value={form.title} onChange={set("title")} testid="order-title" required />
          <div className="border border-[#ED1F26]/30 bg-[#ED1F26]/5 p-3">
            <Select label="Notificar a (aviso crítico) *" value={notifyEmail} onChange={(e) => setNotifyEmail(e.target.value)} testid="order-notify-select">
              <option value="">Selecciona un usuario…</option>
              {notifyUsers.map((u) => <option key={u.email} value={u.email}>{u.name}</option>)}
            </Select>
            <p className="font-mono-data text-xs text-[#5B6672] mt-1.5">
              El usuario recibirá un aviso inmediato en pantalla que deberá confirmar de enterado.
            </p>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <Select label="Asignar remisión" value={form.remision_id} onChange={(e) => selectRemision(e.target.value)} testid="order-remision-select">
              <option value="">Sin remisión</option>
              {remisiones.map((r) => <option key={r.id} value={r.id}>{r.number} · {r.client?.name || "—"}</option>)}
            </Select>
            <label className="block">
              <span className="overline block mb-1.5">Estatus</span>
              <select
                value={form.status}
                onChange={set("status")}
                data-testid="order-status-select"
                className={`w-full border px-3 py-2 font-mono-data text-sm font-bold focus:outline-none focus:ring-1 focus:ring-[#465B7F] ${STATUS_STYLES[form.status] || ""}`}
              >
                {STATUSES.map((s) => <option key={s} value={s} className="bg-white text-[#16202B] font-normal">{s}</option>)}
              </select>
            </label>
          </div>
          <OrderItemsEditor items={items} setItems={setItems} products={products} onCreateProduct={createProduct} />
          <label className="block">
            <span className="overline block mb-1.5">Notas</span>
            <textarea value={form.description} onChange={set("description")} rows={2} data-testid="order-description"
              className="w-full bg-[#FFFFFF] border border-[#E3E8EE] px-3 py-2 font-mono-data text-sm focus:outline-none focus:ring-1 focus:ring-[#465B7F]" />
          </label>
          <TagsField tags={tags} setTags={setTags} testid="order-tags-input" />
          <div className="grid grid-cols-3 gap-4">
            <Input label="Responsable del trabajo" value={form.assigned_to} onChange={set("assigned_to")} testid="order-assigned" />
            <Input label="Fecha del día" type="date" value={createdDate} disabled className="bg-[#F3F4F6] text-[#6B7280]" testid="order-created-date" />
            <Input label="Fecha de entrega" type="date" value={form.due_date} onChange={set("due_date")} testid="order-due" />
          </div>
          <div className="flex justify-end gap-3">
            <Btn variant="ghost" type="button" onClick={() => setOpen(false)}>Cancelar</Btn>
            <Btn type="submit" data-testid="save-order-button">{editing ? "Guardar cambios" : "Crear orden"}</Btn>
          </div>
        </form>
      </Modal>

      <ConfirmDialog
        open={!!deleteOpen}
        onClose={() => setDeleteOpen(null)}
        onConfirm={remove}
        title="Anular orden de trabajo"
        message={`¿Anular la orden ${deleteOpen?.number}? Cambiará a estatus "Anulado" y conservará su folio (no se elimina).`}
        confirmLabel="Anular orden"
        testid="confirm-delete-order-button"
      />

      <ConfirmDialog
        open={bulkOpen}
        onClose={() => setBulkOpen(false)}
        onConfirm={bulkRemove}
        title="Anular órdenes de trabajo"
        message={`¿Anular ${sel.selectedIds.length} orden(es)? Cambiarán a estatus "Anulado" y conservarán su folio.`}
        confirmLabel="Anular seleccionados"
        testid="confirm-bulk-delete-ordenes-button"
      />

      <ImportDialog open={importOpen} onClose={() => setImportOpen(false)} module="work-orders" title="Importar órdenes de trabajo" onDone={loadOrders} />

      <Modal open={!!assignOpen} onClose={() => setAssignOpen(null)} title={`Asignar remisión · ${assignOpen?.number || ""}`}>
        <div className="space-y-4">
          <Select label="Remisión de origen" value={assignRid} onChange={(e) => setAssignRid(e.target.value)} testid="assign-remision-select">
            <option value="">— Sin remisión —</option>
            {remisiones.map((r) => (
              <option key={r.id} value={r.id}>{r.number} · {r.client?.name || "Sin cliente"}</option>
            ))}
          </Select>
          <p className="font-mono-data text-xs text-[#5B6672]">
            Selecciona la remisión a la que pertenece esta orden. Puedes cambiarla o quitarla en cualquier momento.
          </p>
          <div className="flex justify-end gap-3">
            <Btn variant="ghost" type="button" onClick={() => setAssignOpen(null)}>Cancelar</Btn>
            <Btn onClick={saveAssign} data-testid="save-assign-remision-button">Guardar</Btn>
          </div>
        </div>
      </Modal>
    </>
  );
}

function Th({ children, className = "" }) {
  return <th className={`overline font-normal px-4 py-3 ${className}`}>{children}</th>;
}
function Td({ children, className = "", onClick }) {
  return <td onClick={onClick} className={`font-mono-data text-sm text-[#5C5A55] px-4 py-3 ${className}`}>{children}</td>;
}
